import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faInfoCircle, faPaperclip, faExclamationCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import DeleteTimelineEventButton from '../../../common/DeleteTimelineEventButton';
const { rootUrl } = require('../../../../config/config');

const iconForType = (type) => {
    if (!type) return faInfoCircle;
    const t = type.toLowerCase();
    if (t.includes('inspect')) return faCalendarAlt;
    if (t.includes('attach') || t.includes('file')) return faPaperclip;
    if (t.includes('alert') || t.includes('warning')) return faExclamationCircle;
    return faInfoCircle;
};

const formatMonthDay = (iso) => {
    try {
        if (!iso) return '';
        const d = new Date(iso);
        return d.toLocaleString('en-US', { month: 'short', day: 'numeric' });
    } catch (e) {
        return iso;
    }
};

const formatTime = (iso) => {
    try {
        if (!iso) return '';
        const d = new Date(iso);
        return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch (e) {
        return iso;
    }
};

const CarTimeline = ({ plate_no }) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const removeEvent = (id) => setEvents((prev) => prev.filter((e) => e.id !== id));

    useEffect(() => {
        if (!plate_no) return;

        const token = localStorage.getItem('authToken');
        const end = new Date();
        // Use next day as end_date to ensure events from today (later times) are included
        const endPlusOne = new Date(end);
        endPlusOne.setDate(endPlusOne.getDate() + 1);
        const start = new Date();
        start.setDate(end.getDate() - 30);

        const start_date = start.toISOString().split('T')[0];
        const end_date = endPlusOne.toISOString().split('T')[0];

        const url = `${rootUrl.replace(/\/$/, '')}/cars/timeline/plate/${encodeURIComponent(plate_no)}/range?start_date=${start_date}&end_date=${end_date}&status=active`;

        setLoading(true);
        fetch(url, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: token ? `Bearer ${token}` : ''
            }
        })
            .then((res) => {
                if (!res.ok) throw new Error('Failed to fetch timeline events');
                return res.json();
            })
            .then((data) => {
                const list = Array.isArray(data) ? data : data.data || [];
                list.sort((a, b) => new Date(b.event_date || b.input_date || b.date) - new Date(a.event_date || a.input_date || a.date));
                setEvents(list);
                setError(null);
            })
            .catch((err) => {
                setError(err.message || 'Error fetching timeline');
                setEvents([]);
            })
            .finally(() => setLoading(false));
    }, [plate_no]);

    return (
        <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-gray-500">Showing recent activity for {plate_no}</div>
            </div>

            <div className="bg-gray-50 rounded-sm p-4 relative">

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-[#FF5733] mr-3" />
                        <div className="text-gray-600">Loading timeline...</div>
                    </div>
                ) : error ? (
                    <div className="text-red-600 text-center py-8">{error}</div>
                ) : events.length === 0 ? (
                    <div className="text-center py-8 text-gray-600">No timeline events in the last 30 days.</div>
                ) : (
                    <div className="space-y-6">
                        {events.map((ev) => (
                            <div key={ev.id || `${ev.event_date}-${ev.title}`} className="flex flex-col sm:flex-row items-start gap-6">
                                {/* Left: icon + month/day + time (stacked on mobile) */}
                                <div className="w-full sm:w-36 md:w-40 lg:w-48 flex sm:flex-col flex-row items-center sm:items-center justify-between sm:justify-center text-center">
                                    <div className="w-10 h-10 rounded-full bg-white border-2 border-[#fff8f5] flex items-center justify-center shadow-none text-[#FF5733]">
                                        <FontAwesomeIcon icon={iconForType(ev.event_type)} className="text-sm" />
                                    </div>
                                    <div className="mt-3 sm:mt-3 sm:ml-0 ml-3">
                                        <div className="text-sm font-bold text-[#FF5733]">{formatMonthDay(ev.event_date || ev.input_date || ev.date)}</div>
                                        <div className="text-xs text-gray-500">{formatTime(ev.event_date || ev.input_date || ev.date)}</div>
                                    </div>
                                </div>

                                {/* Right: card */}
                                <div className="flex-1 bg-white rounded-sm p-4 shadow-none border border-gray-100 mt-3 sm:mt-0">
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm font-semibold text-[#2a2e30]">{ev.title || ev.event_type || 'Event'}</div>
                                        <div className="flex items-center gap-2">
                                            <div className="text-xs text-gray-400">{ev.location || ev.source || ''}</div>
                                            {ev.id && (
                                                <DeleteTimelineEventButton
                                                    deleteUrl={`${rootUrl.replace(/\/$/, '')}/cars/timeline/${ev.id}`}
                                                    eventTitle={ev.title || ev.event_type}
                                                    onDeleted={() => removeEvent(ev.id)}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    {ev.description && <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">{ev.description}</div>}
                                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                                        <div>Type: <span className="font-medium text-gray-700">{ev.event_type || 'n/a'}</span></div>
                                        {ev.created_by && <div>By: <span className="font-medium text-gray-700">{ev.created_by}</span></div>}
                                        {ev.attachments && ev.attachments.length > 0 && (
                                            <div className="flex items-center gap-1"><FontAwesomeIcon icon={faPaperclip} /><span> {ev.attachments.length} attachment(s)</span></div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CarTimeline;
